'use client';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { pageDirectories } from '@/constant/route';
import { useLoadingStore } from '@/hooks/use-loading-store';
import { performSignOut } from '@/lib/perform-sign-out';
import { cn, isDevEnv } from '@/lib/utils';
import { useRouter } from '@/navigation';
import { useAuthenticationStore } from '@/provider/customs/useAuthenticationStore';
import { getUrlGoToCustRoute } from '@/server/api/v1/auth/auth.action';
import { AuthRole } from '@/types/enum';
import { DropdownMenuArrow } from '@radix-ui/react-dropdown-menu';
import { CircleUser, Loader2, LogIn, LogOut, UserRound } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Fragment, useEffect, useState } from 'react';
import { CustomLayoutHeaderProps } from '../../types';

export function CustomHeaderDropdownMenu({
    className,
    ...props
}: CustomLayoutHeaderProps) {
    const transDef = useTranslations('System');

    const authData = useAuthenticationStore((x) => x.auth);
    const setAppLoading = useLoadingStore((x) => x.setAppLoading);

    // const version = useLayoutStore((state) => state.version);
    // const setVersionLayout = useLayoutStore((state) => state.setVersion);

    // const onSetLayoutVersion = () => {
    //     setVersionLayout(version === 'modern' ? 'legacy' : 'modern');
    // };

    const router = useRouter();
    const onGoToProfile = () => {
        router.push(pageDirectories['ProfileInfo']);
    };

    const [isSigningOut, setIsSigningOut] = useState(false);
    const onSignOut = async () => {
        if (isSigningOut) return; // chống bấm kép trong lúc đang đăng xuất
        setIsSigningOut(true);
        setAppLoading(true); // overlay toàn trang để người dùng biết đang xử lý
        // Dùng window.location.replace thay router.push: buộc full page reload để middleware
        // kiểm tra lại session đã xóa và React state (bao gồm auth store) được reset hoàn toàn.
        // router.push chỉ là client-side navigation — người dùng vẫn thấy trang cũ cho đến F5.
        try {
            const result = await performSignOut({ redirect: false });
            window.location.replace(result.url || pageDirectories['Signin']);
        } catch {
            // performSignOut đã bọc lỗi nội bộ; phòng thủ thêm để chắc chắn luôn về trang đăng nhập.
            window.location.replace(pageDirectories['Signin']);
        }
    };

    const onGoToCust = async () => {
        const url = await getUrlGoToCustRoute();
        if (url?.data) window.location.href = url.data;
    };
    const roles = authData?.Roles?.split(',') || [];
    const isShowBtnToCust = roles.length > 1 && roles.includes(AuthRole.user);

    const [showName, setShowName] = useState<string | undefined>();

    useEffect(() => {
        setShowName(
            authData?.ExtraInfo?.CustomerInfo?.FullName ||
                authData?.ExtraInfo?.UserInfo?.FullName ||
                authData?.UserName
        );
    }, [authData?.ExtraInfo, authData?.UserName]);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant='secondary'
                    size='icon'
                    className={cn('rounded-full', className)}
                >
                    <CircleUser className='h-5 w-5' />
                    <span className='sr-only'>Toggle user menu</span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align='end'
                className='w-52 [&_[role="menuitem"]]:cursor-pointer [&_[role="menuitem"]]:py-2'
            >
                <DropdownMenuArrow className='fill-gray-100' />

                <DropdownMenuItem onClick={onGoToProfile}>
                    <div
                        role='button'
                        className='line-clamp-1 flex max-w-full items-center gap-2 font-semibold'
                    >
                        <UserRound
                            className='h-4 w-4 min-w-4'
                            strokeWidth={2}
                            size={16}
                        />
                        <p className='line-clamp-1 break-words'>
                            {authData?.IsAdmin ? 'PSC' : showName}
                        </p>
                    </div>
                </DropdownMenuItem>

                {!!authData ? (
                    <Fragment>
                        <DropdownMenuSeparator />
                        {isShowBtnToCust && (
                            <DropdownMenuItem
                                onClick={onGoToCust}
                                className='cursor-pointer'
                            >
                                <LogIn size={16} className='mr-2' />
                                Nộp hồ sơ
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                            onClick={onSignOut}
                            onSelect={(e) => e.preventDefault()}
                            disabled={isSigningOut}
                            className='font-medium text-red-500 hover:text-red-600'
                        >
                            {isSigningOut ? (
                                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            ) : (
                                <LogOut className='mr-2 h-4 w-4' />
                            )}
                            <span>{transDef('System_Logout')}</span>
                        </DropdownMenuItem>
                    </Fragment>
                ) : null}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
